import './trade-details-panel.scss'

import styled from '@emotion/styled'
import TradeDeleteModal from 'components/core-v2/organisms/draft-trade-delete-modal'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  accountStepToDisplayName,
  getBannerStateFromTradeStatuStep,
  getNoteCollateralMessage
} from 'types/tradeApproval'

import { LinkButton, MessageBox, Space, Tabs, Typography } from '../../components/core-v2'
import Icon from '../../components/core-v2/atoms/Icon'
import ToastNotification from '../../components/core-v2/atoms/toast-notification/toastNotification'
import TradeDocumentsModal from '../../components/core-v2/organisms/trade-documents-modal'
import { PATH } from '../../const'
import { UserDisplayInfo } from '../../types/user'
import { actions } from '../tradebuilder-v2/modules/00-builder/state/builder-slice'
import NoteDetailsSummary from '../tradebuilder-v2/modules/01-note/details-summary/note-details-summary'
import TermsDetailsSummary from '../tradebuilder-v2/modules/02-terms/details-summary/terms-details-summary'
import { FundingSource } from '../tradebuilder-v2/modules/02-terms/state/terms-slice'
import ChartsDetailsSummary from '../tradebuilder-v2/modules/03-portfolio/details-summary/charts-details-summary'
import EconomicsDetailsSummary from '../tradebuilder-v2/modules/03-portfolio/details-summary/economics-details-summary'
import TradeEditPortfolioDetailsSummary from '../tradebuilder-v2/modules/03-portfolio/details-summary/edit-portfolio-details-summary'
import PortfolioDetailsSummary from '../tradebuilder-v2/modules/03-portfolio/details-summary/portfolio-details-summary'
import TradeEditStrategyDetailsSummary from '../tradebuilder-v2/modules/04-strategy/details-summary/edit-strategy-details-summary'
import StrategyDetailsSummary from '../tradebuilder-v2/modules/04-strategy/details-summary/strategy-details-summary'
import { useAppDispatch, useAppSelector } from '../tradebuilder-v2/store/hooks'
import { TradeActivationStep } from '../tradebuilder-v2/types'
import TradeActivationLayout from './trade-activation-layout'

export type TradeDetailsPanelProps = {
  onDelete: (accountId: string) => void
  onStatusChange: (accountId: string) => void
  userInfo: UserDisplayInfo
}

const StyledFixScrollingArea = styled.div`
  overflow-y: scroll;
  height: calc(100vh - 385px);
`

const StyledMaxWidthArea = styled.div`
  max-width: 1000px;
  margin-inline: auto;
`

const TradeDetailsPanel = ({ onDelete, onStatusChange, userInfo }: TradeDetailsPanelProps) => {
  const [selectedTab, setSelectedTab] = useState<string>('activation-process')
  const [showDocumentation, setShowDocumentation] = useState<boolean>(false)
  const [showDeleteBox, setShowDeleteBox] = useState<boolean>(false)
  const builderState = useAppSelector(x => x.builder)
  const existingNote = useAppSelector(x => x.existingNote)
  const termsState = useAppSelector(x => x.terms)
  const selectedAccount = builderState.savedState?.account
  let selectedAccountStatus = builderState.status
  if (
    termsState.terms.fundingSource === FundingSource.Switch &&
    builderState.status === TradeActivationStep.settlement
  ) {
    selectedAccountStatus = TradeActivationStep.activate
  }
  if (
    termsState.terms.fundingSource === FundingSource.Switch &&
    builderState.status === TradeActivationStep.activate
  ) {
    selectedAccountStatus = TradeActivationStep.active
  }
  const selectedAccountStatusName = accountStepToDisplayName(selectedAccountStatus)
  const noteCollateralMessage = getNoteCollateralMessage(selectedAccountStatus, existingNote.note)
  const statusName = `${selectedAccountStatusName} ${noteCollateralMessage}`

  const isDraftTrade = selectedAccountStatus === TradeActivationStep.draft
  const isRejectedTrade = selectedAccountStatus === TradeActivationStep.rejected
  const userType = userInfo?.userType
  const userRoles = userInfo?.userRoles
  const userCanEdit =
    userType === 'admin' ||
    (userType === 'absa' &&
      (userRoles.includes('BankAdmin') ||
        userRoles.includes('FLStructuring') ||
        userRoles.includes('Sales'))) ||
    (userType === 'business-unit' && userRoles.includes('Editor'))
  const bannerState = getBannerStateFromTradeStatuStep(selectedAccountStatus)
  const isNewTrade = selectedAccount?.newAccount
  const isLiveEditedPendingApprovalTrade =
    !isNewTrade && builderState.status !== TradeActivationStep.active

  const showEditNewTrade =
    selectedAccount &&
    selectedAccount.newAccount === true &&
    (isDraftTrade || isRejectedTrade) &&
    userCanEdit
  const showEditLiveTrade =
    selectedAccount &&
    selectedAccount.newAccount === false &&
    (isDraftTrade || isRejectedTrade) &&
    userCanEdit
  const showTradeDocumentation = !isDraftTrade && !isRejectedTrade

  const approvalStateTradeDocumentationRelativeUrl = builderState.savedState?.tradeDocumentationUrl

  const dispatch = useAppDispatch()
  useEffect(() => {
    if (selectedAccount) {
      const defaultTab = isDraftTrade ? 'note' : 'activation-process'
      setSelectedTab(defaultTab)
    }
  }, [dispatch, selectedAccount, isDraftTrade])

  useEffect(() => {
    if (builderState.approvalNotification !== undefined) {
      onStatusChange(builderState.accountId)
    }
  }, [builderState.approvalNotification, builderState.accountId, onStatusChange])

  const onNotificationClose = () => {
    dispatch(actions.resetNotification())
  }

  const navigate = useNavigate()

  const onConfirmDeleteDraftTrade = () => {
    setShowDeleteBox(undefined)
    onDelete(selectedAccount.accountId)
    dispatch(actions.setSelectedAccountId(''))
    navigate(PATH.trades)
  }

  const renderAction = () => (
    <>
      {(showEditNewTrade || showEditLiveTrade) && (
        <Space isinlineblock>
          <Space isflex alignItems="center">
            <Icon name="bin" size="default" />
            <Space ml={2}>
              <LinkButton underline onClick={() => setShowDeleteBox(true)}>
                Delete this trade
              </LinkButton>
            </Space>
          </Space>
        </Space>
      )}
      {showEditNewTrade && (
        <Space isinlineblock ml={4}>
          <Space isflex alignItems="center">
            <Icon name="squareInput" size="default" />
            <Space ml={2}>
              <LinkButton
                underline
                onClick={() => navigate(`/trades/build/${selectedAccount.accountId}`)}
              >
                Edit this trade
              </LinkButton>
            </Space>
          </Space>
        </Space>
      )}
      {showEditLiveTrade && (
        <Space isinlineblock ml={4}>
          <Space isflex alignItems="center">
            <Icon name="squareInput" size="default" />
            <Space ml={2}>
              <LinkButton
                underline
                onClick={() => navigate(`/trades/edit/${selectedAccount.accountId}`)}
              >
                Edit this trade
              </LinkButton>
            </Space>
          </Space>
        </Space>
      )}
      {showTradeDocumentation && (
        <Space isflex alignItems="center">
          <Icon name="document" size="default" />
          <Space ml={2}>
            <LinkButton underline onClick={() => setShowDocumentation(true)}>
              View documentation
            </LinkButton>
          </Space>
        </Space>
      )}
    </>
  )

  return selectedAccount ? (
    <>
      <Space mx={5} pt={6}>
        <Typography fontSize="15px" fontWeight="bold" letterSpacing="0px" variant="b3">
          {selectedAccount.accountName}
        </Typography>
        <Space mt={6}>
          <MessageBox showStateCircle actionRenderer={renderAction()} state={bannerState}>
            {statusName}
          </MessageBox>
        </Space>
      </Space>
      <Tabs.List className="mt-4 mx-4" spacing="md">
        {!isDraftTrade && (
          <Tabs.Tab
            isSelected={selectedTab === 'activation-process'}
            value="activation-process"
            onClick={() => setSelectedTab('activation-process')}
          >
            Activation process
          </Tabs.Tab>
        )}
        <Tabs.Tab
          isSelected={selectedTab === 'note'}
          value="note"
          onClick={() => setSelectedTab('note')}
        >
          Note
        </Tabs.Tab>
        <Tabs.Tab
          isSelected={selectedTab === 'terms'}
          value="terms"
          onClick={() => setSelectedTab('terms')}
        >
          Terms
        </Tabs.Tab>
        <Tabs.Tab
          isSelected={selectedTab === 'portfolio'}
          value="portfolio"
          onClick={() => setSelectedTab('portfolio')}
        >
          Portfolio
        </Tabs.Tab>
        <Tabs.Tab
          isSelected={selectedTab === 'strategy'}
          value="strategy"
          onClick={() => setSelectedTab('strategy')}
        >
          Strategy
        </Tabs.Tab>
        <Tabs.Tab
          isSelected={selectedTab === 'economics'}
          value="economics"
          onClick={() => setSelectedTab('economics')}
        >
          Economics
        </Tabs.Tab>
        <Tabs.Tab
          isSelected={selectedTab === 'graphs'}
          value="graphs"
          onClick={() => setSelectedTab('graphs')}
        >
          Graphs
        </Tabs.Tab>
      </Tabs.List>
      <StyledFixScrollingArea>
        <StyledMaxWidthArea>
          {selectedTab === 'activation-process' && (
            <Tabs.Panel value="activation-process">
              <TradeActivationLayout />
            </Tabs.Panel>
          )}
          {selectedTab === 'note' && (
            <Tabs.Panel value="note">
              <NoteDetailsSummary />
            </Tabs.Panel>
          )}
          {selectedTab === 'terms' && (
            <Tabs.Panel value="terms">
              <TermsDetailsSummary />
            </Tabs.Panel>
          )}
          {selectedTab === 'portfolio' && (
            <Tabs.Panel value="portfolio">
              {isLiveEditedPendingApprovalTrade && <TradeEditPortfolioDetailsSummary />}
              {!isLiveEditedPendingApprovalTrade && <PortfolioDetailsSummary />}
            </Tabs.Panel>
          )}
          {selectedTab === 'strategy' && (
            <Tabs.Panel value="strategy">
              {isLiveEditedPendingApprovalTrade && <TradeEditStrategyDetailsSummary />}
              {!isLiveEditedPendingApprovalTrade && <StrategyDetailsSummary />}
            </Tabs.Panel>
          )}
          {selectedTab === 'economics' && (
            <Tabs.Panel value="economics">
              <EconomicsDetailsSummary />
            </Tabs.Panel>
          )}
          {selectedTab === 'graphs' && (
            <Tabs.Panel value="graphs">
              <ChartsDetailsSummary />
            </Tabs.Panel>
          )}
          {showDocumentation && (
            <TradeDocumentsModal
              tradeDocumentationUrl={approvalStateTradeDocumentationRelativeUrl}
              onClose={() => setShowDocumentation(undefined)}
            />
          )}
          {showDeleteBox && (
            <TradeDeleteModal
              onClose={() => setShowDeleteBox(undefined)}
              onDelete={() => onConfirmDeleteDraftTrade()}
            />
          )}
        </StyledMaxWidthArea>
        {builderState && builderState.approvalNotification && (
          <ToastNotification
            description={builderState.approvalNotification.description}
            notificationType="success"
            open={builderState.approvalNotification !== undefined}
            title={builderState.approvalNotification.title}
            onClose={() => onNotificationClose()}
          />
        )}
      </StyledFixScrollingArea>
    </>
  ) : null
}

export default TradeDetailsPanel
